import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { requestHandler } from 'lib/api';
import { errorHandler } from 'lib/functions/errors';
import { Dispatch } from 'redux';

import { EmitWarrantyInvoiceBody, InvoicingReponse, Principal } from './types';

export const getSGRWarrantyRequests = async (
  dispatch: Dispatch,
  query: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/sgrWarrantyRequest?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessSGRWarrantyRequests = async (
  dispatch: Dispatch,
  businessIdentifier: string,
  query: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/sgrWarrantyRequest/${businessIdentifier}?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getSGRWarrantyRequestFile = async (
  dispatch: Dispatch,
  warrantyRequestId: number,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/sgrWarrantyRequest/${warrantyRequestId}/file`,
    dispatch,
    responseType: 'arraybuffer',
  });
  if (response) {
    const blob = new Blob([response.data], { type: 'application/pdf' });
    return {
      blob: blob,
      contentType: 'application/pdf',
    };
  }
};

export const getPublicSGRWarrantyRequestFile = async (
  dispatch: Dispatch,
  secretId: string,
  warrantyRequestId: number,
  clientIdentifier: string,
  principalIdentifier: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `public/express/${secretId}/sgrWarrantyRequestFile/${warrantyRequestId}/${clientIdentifier}/${principalIdentifier}`,
    dispatch,
    responseType: 'arraybuffer',
  });
  if (response) {
    const blob = new Blob([response.data], { type: 'application/pdf' });
    return {
      blob: blob,
      contentType: 'application/pdf',
    };
  }
};

export const getPrincipalInfo = (dispatch: Dispatch, id: number) =>
  useQuery({
    queryKey: ['sgr', 'principalInfo', id],
    queryFn: async (): Promise<Principal | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/sgrWarrantyRequest/principal/${id}`,
        dispatch,
      });
      if (response) return response.data;
    },
  });

export const getSGRWarrantyRequestPayments = async (
  dispatch: Dispatch,
  warrantyRequestId: number,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/sgrWarrantyRequest/${warrantyRequestId}/payments`,
    dispatch,
  });
  if (response) return response.data;
};

export const getSGRWarrantyRequestInvoicing = (
  dispatch: Dispatch,
  id: number,
) =>
  useQuery({
    queryKey: ['sgrWarrantyRequestInvoicing', id],
    queryFn: async (): Promise<InvoicingReponse | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/sgrWarrantyRequest/${id}/invoicing`,
        dispatch,
      });
      if (response) return response.data;
    },
    retry: 1,
    staleTime: 1000 * 60 * 5,
  });

export const createSGRWarrantyRequest = (dispatch: Dispatch) =>
  useMutation({
    mutationKey: ['create', 'sgrWarrantyRequest'],
    mutationFn: async (data: unknown) => {
      const response = await requestHandler({
        method: 'post',
        url: `internal/sgrWarrantyRequest`,
        dispatch,
        data,
      });
      if (response) return response.data;
    },
    onError: (error) => errorHandler(dispatch, error),
  });

export const createSGRWarrantyRequestPruffConnect = (dispatch: Dispatch) =>
  useMutation({
    mutationKey: ['create', 'sgrWarrantyRequest', 'pruffConnect'],
    mutationFn: async (params: { data: unknown; secretId: string }) => {
      const { data, secretId } = params;
      const response = await requestHandler({
        method: 'post',
        url: `internal/express/${secretId}/sgrWarrantyRequest`,
        dispatch,
        data,
      });
      if (response) return response.data;
    },
    onError: (error) => errorHandler(dispatch, error),
  });

export const emitWarrantyInvoice = (dispatch: Dispatch) =>
  useMutation({
    mutationKey: ['emit', 'warrantyInvoice'],
    mutationFn: async (body: EmitWarrantyInvoiceBody) => {
      const response = await requestHandler({
        method: 'post',
        url: `internal/sgrWarrantyRequest/invoice`,
        data: body,
        dispatch,
      });
      if (response) return response.data;
    },
    onError: (error) => errorHandler(dispatch, error),
  });

export const updateSGRWarrantyRequest = (dispatch: Dispatch) =>
  useMutation({
    mutationKey: ['update', 'sgrWarrantyRequest'],
    mutationFn: async (params: {
      warrantyRequestId: number;
      data: unknown;
    }) => {
      const { warrantyRequestId, data } = params;
      const response = await requestHandler({
        method: 'put',
        url: `internal/sgrWarrantyRequest/${warrantyRequestId}`,
        dispatch,
        data,
      });
      if (response) return response.data;
    },
    onError: (error) => errorHandler(dispatch, error),
  });

export const startClaimProcess = async (
  dispatch: Dispatch,
  warrantyRequestId: number,
  data: { claimReason: string },
) => {
  const response = await requestHandler({
    method: 'put',
    url: `internal/sgrWarrantyRequest/${warrantyRequestId}/claim`,
    dispatch,
    data,
  });
  if (response) return response.data;
};

export const deleteSGRWarrantyRequest = async (
  dispatch: Dispatch,
  warrantyRequestId: number,
) => {
  const response = await requestHandler({
    method: 'delete',
    url: `internal/sgrWarrantyRequest/${warrantyRequestId}`,
    dispatch,
  });

  if (response) return response.data;
};
